import { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import {
    format,
    parseISO,
    endOfMonth,
    startOfMonth,
    addDays,
    getDay,
} from 'date-fns';
import { useTranslation } from 'react-i18next';
import { SttDialog, SttButtonMonths, SttButtonYears } from '../all';

/**
 * Dialog calendar
 * ===============
 *
 * Props
 *
 *     * boolean open
 *     * string date: Y-m-d
 *     * function onClickDate(date): Y-m-d
 *     * function onClose
 */

type CalendarCellData = {
    cap: string;
    date?: string;
};

type CalendarWeek = {
    days: CalendarCellData[];
};

type Props = {
    open?: boolean;
    date?: string;
    onSelectDate?: (date: string) => void;
    onClose: () => void;
};

export function SttCalendarDialog({
    open = false,
    date,
    onSelectDate,
    onClose,
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const [year, setYear] = useState<number>(new Date().getFullYear());
    const [month, setMonth] = useState<number>(new Date().getMonth() + 1);

    const calendarHeader = [
        t('day.1.mini'),
        t('day.2.mini'),
        t('day.3.mini'),
        t('day.4.mini'),
        t('day.5.mini'),
        t('day.6.mini'),
        t('day.0.mini'),
    ];

    /**
     * Calendar struct
     *
     *      [
     *        {
     *            days: [
     *                {cap:'L'},
     *                {cap:'M'},
     *                ...
     *            ]
     *        },
     *        {
     *            days: [
     *                {cap:""},
     *                {cap:"1", date:"2020-02-01"},
     *                {cap:"2", date:"2020-02-02"},
     *                  ...
     *              ]
     *          }
     *      ]
     */
    const [calendar, setCalendar] = useState<CalendarWeek[]>();

    /**
     * Crear datos del calendario
     */
    const createCalendar = () => {
        const dateStr = `${year}-${month.toString().padStart(2, '0')}-01`;
        let currentDate = parseISO(dateStr);

        const numDaysOfMonth = parseInt(format(endOfMonth(currentDate), 'd'));
        // getDay returns 0 for Sunday, we need 1-7 (Mon-Sun), so convert
        const firstDayOfWeek = getDay(startOfMonth(currentDate));
        // Convert to ISO week day (Mon=1, Sun=7)
        const firstDayMonth = firstDayOfWeek === 0 ? 7 : firstDayOfWeek;

        // Init calendar
        const cal: CalendarWeek[] = [];

        // Add cells
        let week: CalendarWeek = { days: [] };
        let currentCell = 1;
        let currentDay = 0;
        do {
            currentDay = currentCell - firstDayMonth + 1;

            if (currentDay > 0) {
                week.days.push({
                    cap: format(currentDate, 'd'),
                    date: format(currentDate, 'yyyy-MM-dd'),
                });
                currentDate = addDays(currentDate, 1);
            } else {
                week.days.push({ cap: '' });
            }

            // Next week
            if (week.days.length === 7) {
                cal.push(week);
                week = { days: [] };
            } else if (currentDay >= numDaysOfMonth) {
                // If we're over, add the next week filling the empty days
                week = {
                    days: week.days.concat(
                        new Array(7 - week.days.length).fill({ cap: '' }),
                    ),
                };
                cal.push(week);
            }

            currentCell++;
        } while (currentDay < numDaysOfMonth);

        setCalendar(cal);
    };

    /**
     * Click on cell
     */
    const clickOnCell = (cell: CalendarCellData) => {
        if (cell.date && onSelectDate) {
            onSelectDate(cell.date);
        }
    };

    /**
     * Year, month change
     */
    useEffect(() => {
        if (year && month) {
            createCalendar();
        }
    }, [year, month]);

    // Render
    return (
        <SttDialog
            title={t('Calendar')}
            open={open}
            onClose={() => onClose()}
            content={
                <>
                    <Box mb={2} sx={{ textAlign: 'center' }}>
                        <SttButtonYears
                            year={year}
                            radius={3}
                            onSelectYear={setYear}
                        />
                    </Box>

                    <Box mb={2} sx={{ textAlign: 'center' }}>
                        <SttButtonMonths
                            contract="xs"
                            tinyLabels
                            selectedMonths={[month]}
                            onChangeMonth={setMonth}
                        />
                    </Box>

                    <Box style={{ textAlign: 'center', marginTop: 15 }}>
                        <Box style={{ display: 'flex' }}>
                            {calendarHeader.map((value: any, i: any) => (
                                <CalendarHeaderCell key={i} value={value} />
                            ))}
                        </Box>
                        {calendar?.map((week, numWeek) => (
                            <Box key={numWeek} style={{ display: 'flex' }}>
                                {week &&
                                    week.days &&
                                    week.days.map((cell: any, i: any) => (
                                        <CalendarCell
                                            key={i}
                                            cell={cell}
                                            date={date}
                                            onClick={() => clickOnCell(cell)}
                                        />
                                    ))}
                            </Box>
                        ))}
                    </Box>
                </>
            }
            buttons={
                [
                    {
                        type: 'close',
                        onClick: onClose,
                    },
                ] as any
            }
        />
    );
}

type CalendarHeaderCellProps = {
    value: string;
};

function CalendarHeaderCell({
    value,
}: CalendarHeaderCellProps): React.ReactElement {
    return (
        <Box
            sx={{
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: 0.25,
                cursor: 'default',
                backgroundColor: '#eaeaea',
                height: 30,
                userSelect: 'none',
            }}
        >
            {value}
        </Box>
    );
}

/**
 * Calendar cell
 */
type CalendarCellProps = {
    cell: CalendarCellData;
    date?: string;
    onClick: () => void;
};

function CalendarCell({
    cell,
    date,
    onClick,
}: CalendarCellProps): React.ReactElement {
    const backgroundColor =
        date && date === cell.date
            ? '#c5c5c5'
            : cell.cap
              ? '#eaeaea'
              : 'transparent';

    return (
        <Box
            onClick={onClick}
            sx={{
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: 50,
                margin: 0.25,
                cursor: 'pointer',
                userSelect: 'none',
                backgroundColor,
            }}
        >
            {cell.cap}
        </Box>
    );
}
